import { useState, useCallback, useRef, useEffect } from "react";
import { IState } from "country-state-city";
import { FormState } from "../components/CheckoutForm";
import { validateField, validateAllFields, FieldErrors } from "../utils/validateBillingForm";

interface UseFormValidationParams {
  formData: FormState;
  states: IState[];
  debounceMs?: number;
}

interface UseFormValidationReturn {
  fieldErrors: FieldErrors;
  touchedFields: Set<keyof FormState>;
  handleBlur: (field: keyof FormState, value?: string) => void;
  handleFieldChange: (field: keyof FormState, value: string) => void;
  validateAllOnSubmit: () => boolean;
  clearFieldError: (field: keyof FormState) => void;
  clearAllErrors: () => void;
}

export function useFormValidation({ formData, states, debounceMs = 500 }: UseFormValidationParams): UseFormValidationReturn {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof FormState>>(new Set());
  const debounceTimers = useRef<Partial<Record<keyof FormState, NodeJS.Timeout>>>({});

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((timer) => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const validateAndSetError = useCallback(
    (field: keyof FormState, value: string) => {
      const error = validateField({ field, value, states });
      setFieldErrors((prev) => {
        if (error) {
          return { ...prev, [field]: error };
        }
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    },
    [states],
  );

  const handleBlur = useCallback(
    (field: keyof FormState, value?: string) => {
      // Mark field as touched
      setTouchedFields((prev) => new Set(prev).add(field));

      // Clear any pending debounce timer for this field
      if (debounceTimers.current[field]) {
        clearTimeout(debounceTimers.current[field]);
        delete debounceTimers.current[field];
      }

      // Validate immediately on blur - use provided value or fall back to formData
      const valueToValidate = value !== undefined ? value : formData[field];
      validateAndSetError(field, valueToValidate);
    },
    [formData, validateAndSetError],
  );

  const handleFieldChange = useCallback(
    (field: keyof FormState, value: string) => {
      // Only validate while typing if the field has been touched (blurred at least once)
      if (!touchedFields.has(field)) {
        return;
      }

      // Clear existing timer for this field
      if (debounceTimers.current[field]) {
        clearTimeout(debounceTimers.current[field]);
      }

      // Set new debounced validation
      debounceTimers.current[field] = setTimeout(() => {
        validateAndSetError(field, value);
        delete debounceTimers.current[field];
      }, debounceMs);
    },
    [touchedFields, debounceMs, validateAndSetError],
  );

  const validateAllOnSubmit = useCallback((): boolean => {
    // Mark all fields as touched
    const allFields: (keyof FormState)[] = ["firstName", "lastName", "address1", "city", "state", "postalCode", "country"];
    setTouchedFields(new Set(allFields));

    // Validate all fields
    const errors = validateAllFields({ formData, states });
    setFieldErrors(errors);

    return Object.keys(errors).length === 0;
  }, [formData, states]);

  const clearFieldError = useCallback((field: keyof FormState) => {
    setFieldErrors((prev) => {
      const { [field]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
    setTouchedFields(new Set());
  }, []);

  return {
    fieldErrors,
    touchedFields,
    handleBlur,
    handleFieldChange,
    validateAllOnSubmit,
    clearFieldError,
    clearAllErrors,
  };
}
